import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dropdown, Form, Input, Menu, Tooltip } from '@arco-design/web-react';
import { IconDelete, IconPlus, IconQuestionCircle } from '@arco-design/web-react/icon';

import { safeParseJSON } from '@/utils/json-parse';

import { buildPresetListKeySet, resolveNamedListItemKey } from '../../platform/access-policy/orderListItemLock';
import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { useOptionalResourceFormContext } from '../../platform/resource-form';
import { STARTUP_COMMAND_ARGS_ACTION_KEYS, STARTUP_COMMAND_ARGS_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { StartupArgsOnlyBlockProps, StartupCommandArgsBlockProps } from './types';
import type { StartupArgDefinition, StartupArgFormRow, StartupArgMeta } from './utils';

const FormItem = Form.Item;
const FormList = Form.List;
const { TextArea } = Input;

function isCustomStartupArgRow(def: StartupArgMeta | null, row?: StartupArgFormRow) {
  if (def?.custom) return true;
  if (!row?._self?.trim()) return true;
  return false;
}

function requiredRules(message: string) {
  return [{ required: true, message: '值必填' }];
}

function ensureStartupArgsListField(form: StartupArgsOnlyBlockProps['form'], listPath: string) {
  const current = form.getFieldValue(listPath);
  if (current === undefined || current === null) {
    form.setFieldValue(listPath, []);
  }
}

/** 启动参数列表（可单独用于 maas 等场景） */
export function StartupArgsBlock(props: StartupArgsOnlyBlockProps) {
  const { form, paths, options, linkageProps, ...control } = props;
  const { t } = useTranslation();
  const argDefinitions = options?.argDefinitions ?? (linkageProps?.argDefinitions as StartupArgDefinition[] | undefined) ?? [];
  const noStyle = options?.noStyle === true;
  const readonly = isFieldReadonly(STARTUP_COMMAND_ARGS_FIELD_KEYS.list, control);
  const disabled = isFieldDisabled(STARTUP_COMMAND_ARGS_FIELD_KEYS.list, control);
  /**
   * preview / 显式 readonly：禁增删；
   * 下单 orderListItemLock 时 list 保持 editable，可增，预置行由 skuPresetKeys 禁删改。
   */
  const canAddPreset = isActionVisible(STARTUP_COMMAND_ARGS_ACTION_KEYS.addPreset, control) && !readonly && !disabled;
  const canAddCustom = isActionVisible(STARTUP_COMMAND_ARGS_ACTION_KEYS.addCustom, control) && !readonly && !disabled;
  const canRemove = isActionVisible(STARTUP_COMMAND_ARGS_ACTION_KEYS.remove, control) && !readonly && !disabled;
  const canShowFooter = !readonly && !disabled && (canAddPreset || canAddCustom);
  const resourceFormCtx = useOptionalResourceFormContext();
  const skuPresetKeys = useMemo(
    () => buildPresetListKeySet(resourceFormCtx?.presetConfigSnapshot, paths.list, (row) => resolveNamedListItemKey(row, 'name')),
    [paths.list, resourceFormCtx?.presetConfigSnapshot],
  );

  useEffect(() => {
    ensureStartupArgsListField(form, paths.list);
  }, [form, paths.list]);

  if (!isFieldVisible(STARTUP_COMMAND_ARGS_FIELD_KEYS.list, control)) {
    return null;
  }

  const addCustomArg = (add: (item: StartupArgFormRow) => void) => {
    ensureStartupArgsListField(form, paths.list);
    add({
      name: '',
      value: '',
      _self: JSON.stringify({
        name: '',
        optional: true,
        required: false,
        editable: true,
        visible: true,
        custom: true,
      } satisfies StartupArgMeta),
    });
  };

  return (
    <FormItem label={t('cluster:parameters')} extra={t('components:container.startup.command.parameters.description')} noStyle={noStyle}>
      <FormList field={paths.list}>
        {(fields, { add, remove }) => {
          const rows = (form.getFieldValue(paths.list) || []) as StartupArgFormRow[];
          const usedNames = new Set(rows.map((r) => r?.name).filter(Boolean));
          const presetOptions = argDefinitions.filter((d) => d.optional !== false && d.visible !== false && !usedNames.has(d.name));

          return (
            <div className={styles.panel}>
              <div className={styles.head}>
                <span>{t('cluster:parameters')}</span>
                <span>{t('common:value')}</span>
                <span style={{ opacity: 0 }}>{t('common:operation')}</span>
              </div>

              <div className={styles.container}>
                {fields.length === 0 ? (
                  <div className={styles.empty}>{t('components:container.startup.command.parameters.empty')}</div>
                ) : (
                  fields.map((item, index) => {
                    const row = form.getFieldValue(`${paths.list}[${index}]`) as StartupArgFormRow | undefined;
                    const def = (safeParseJSON(row?._self, null) || {
                      name: row?.name || '',
                      optional: true,
                      required: false,
                      editable: true,
                      visible: true,
                    }) as StartupArgMeta;
                    const isCustom = isCustomStartupArgRow(def, row);
                    const inSkuPreset = skuPresetKeys.has(String(row?.name ?? '').trim());
                    const isHidden = def?.visible === false;
                    const isPinned = inSkuPreset || (!isCustom && def?.optional === false);
                    const nameDisabled = disabled || inSkuPreset || !isCustom;
                    const valueDisabled = disabled || inSkuPreset || (!isCustom && def?.editable === false);
                    const rowReadOnly = readonly || inSkuPreset;
                    const shouldRequireValue = !isCustom && !!def?.required && !valueDisabled && !isHidden;
                    const nameRules = !isHidden && !rowReadOnly && isCustom ? requiredRules(t('common:name')) : undefined;
                    const valueRules = !isHidden && !rowReadOnly && shouldRequireValue ? requiredRules(t('common:value')) : undefined;

                    return (
                      <FormItem key={item.key} hidden={isHidden} style={{ marginBottom: 0 }}>
                        <div className={styles.row}>
                          <div className={styles.nameCell}>
                            <FormItem field={`${item.field}.name`} rules={nameRules}>
                              <Input
                                readOnly={rowReadOnly}
                                disabled={nameDisabled}
                                placeholder={t('common:name')}
                                suffix={
                                  def?.description ? (
                                    <Tooltip content={def.description}>
                                      <IconQuestionCircle className={styles.hintIcon} />
                                    </Tooltip>
                                  ) : undefined
                                }
                              />
                            </FormItem>
                          </div>
                          <FormItem field={`${item.field}.value`} rules={valueRules}>
                            <Input readOnly={rowReadOnly} disabled={valueDisabled} placeholder={t('common:value')} />
                          </FormItem>
                          <FormItem field={`${item.field}._self`} hidden>
                            <Input />
                          </FormItem>
                          <div className={styles.ops}>
                            {canRemove && !isPinned ? (
                              <Button type="text" size="mini" status="danger" icon={<IconDelete />} onClick={() => remove(index)} />
                            ) : null}
                          </div>
                        </div>
                      </FormItem>
                    );
                  })
                )}
              </div>

              {canShowFooter ? (
                <div className={styles.footer}>
                  {canAddPreset && presetOptions.length > 0 ? (
                    <Dropdown
                      droplist={
                        <Menu
                          className={styles.presetMenu}
                          onClickMenuItem={(key) => {
                            ensureStartupArgsListField(form, paths.list);
                            const def = argDefinitions.find((d) => d.name === key);
                            if (def) add({ name: def.name, value: def.defaultValue ?? '', _self: JSON.stringify(def) });
                          }}
                        >
                          {presetOptions.map((def) => (
                            <Menu.Item key={def.name}>
                              <div className={styles.presetMenuItemName}>{def.name}</div>
                              {def.description ? <div className={styles.presetMenuItemDescription}>{def.description}</div> : null}
                            </Menu.Item>
                          ))}
                        </Menu>
                      }
                      trigger="click"
                      position="bl"
                    >
                      <Button type="primary" size="small" icon={<IconPlus />}>
                        {t('components:container.startup.command.parameters.add.preset')}
                      </Button>
                    </Dropdown>
                  ) : null}
                  {canAddCustom ? (
                    <Button type="primary" size="small" icon={<IconPlus />} onClick={() => addCustomArg(add)}>
                      {t('components:container.startup.command.parameters.add.custom')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        }}
      </FormList>
    </FormItem>
  );
}

/** 启动命令 + 启动参数能力块 */
export function StartupCommandArgsBlock(props: StartupCommandArgsBlockProps) {
  const { paths, ...control } = props;
  const { t } = useTranslation();
  const readonly = isFieldReadonly(STARTUP_COMMAND_ARGS_FIELD_KEYS.command, control);
  const disabled = isFieldDisabled(STARTUP_COMMAND_ARGS_FIELD_KEYS.command, control);
  const showCommand = isFieldVisible(STARTUP_COMMAND_ARGS_FIELD_KEYS.command, control);
  const showArgs = isFieldVisible(STARTUP_COMMAND_ARGS_FIELD_KEYS.list, control);

  if (!showCommand && !showArgs) {
    return null;
  }

  return (
    <>
      {showCommand ? (
        <FormItem
          label={t('cluster:command')}
          field={paths.command}
          extra={t('components:container.startup.command.description')}
          disabled={disabled}
        >
          <TextArea
            readOnly={readonly}
            disabled={disabled}
            placeholder={t('components:please.enter.execution.command')}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </FormItem>
      ) : null}
      {showArgs ? <StartupArgsBlock {...props} /> : null}
    </>
  );
}
